"use client"

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2'

// Register ChartJS components for high-precision rendering
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

interface LazyChartProps {
  type: 'bar' | 'line' | 'pie' | 'doughnut'
  title?: string
  labels: string[]
  data: number[]
}

export default function LazyChart({ type, title, labels, data }: LazyChartProps) {
  const chartData = {
    labels,
    datasets: [
      {
        label: title || 'Data Set',
        data,
        backgroundColor: [
          'rgba(196, 164, 132, 0.7)', // Brand Gold/Primary
          'rgba(24, 24, 27, 0.7)',   // Zinc 900
          'rgba(63, 63, 70, 0.7)',   // Zinc 700
          'rgba(161, 161, 170, 0.7)', // Zinc 400
        ],
        borderColor: [
          'rgba(196, 164, 132, 1)',
          'rgba(24, 24, 27, 1)',
          'rgba(63, 63, 70, 1)',
          'rgba(161, 161, 170, 1)',
        ],
        borderWidth: 1,
        tension: 0.4, // For line charts
      },
    ],
  }

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          font: {
            family: 'serif',
            size: 10,
          },
        },
      },
      title: {
        display: !!title,
        text: title,
        font: {
          family: 'serif',
          size: 16,
          weight: 'bold' as const,
        },
      },
    },
    scales: type === 'bar' || type === 'line' ? {
      y: { beginAtZero: true },
    } : {},
  }

  return (
    <div className="w-full bg-muted/20 p-6 rounded-sm border border-border my-10 animate-in fade-in duration-700">
      <div className="max-w-2xl mx-auto">
        {type === 'bar' && <Bar data={chartData} options={options} />}
        {type === 'line' && <Line data={chartData} options={options} />}
        {type === 'pie' && <Pie data={chartData} options={options} />}
        {type === 'doughnut' && <Doughnut data={chartData} options={options} />}
      </div>
      <p className="text-[10px] text-center mt-6 uppercase tracking-widest text-muted-foreground/60 italic font-bold">
        Dynamic Insight Engine • WorldPrimePost Analytical Suite
      </p>
    </div>
  )
}
